import { createPdf, pdfLine, pdfRect, pdfText, wrapPdfText } from './pdf';

const purple = '0.43 0.16 0.85';
const muted = '0.35 0.39 0.48';
const money = value => `INR ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plain = value => String(value).replace(/[\u2013\u2014]/g, '-');

// JPEG is encoded as ASCII hex so PDF stream lengths and byte offsets stay exact.
export function loadEstimateLogo() {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => reject(new Error('Logo loading timed out')), 15000);
    image.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = Math.max(1, Math.round(256 * image.naturalHeight / image.naturalWidth));
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const bytes = atob(canvas.toDataURL('image/jpeg', 0.92).split(',')[1]);
        resolve({ width: canvas.width, height: canvas.height, hex: Array.from(bytes, byte => byte.charCodeAt(0).toString(16).padStart(2, '0')).join('') });
      } catch (error) { reject(error); }
    };
    image.onerror = () => { clearTimeout(timer); reject(new Error('Logo could not be loaded')); };
    image.src = `${process.env.PUBLIC_URL || ''}/logo.png`;
  });
}

export function estimatePdfPages(quote, catalog, annual, logo, date = new Date()) {
  const pages = [];
  let commands;
  let y;
  const text = (value, x, at, options = {}) => commands.push(pdfText(plain(value), x, at, options));
  const startPage = () => {
    commands = [];
    pages.push(commands);
    const scale = Math.min(56 / logo.width, 56 / logo.height);
    commands.push(`q ${logo.width * scale} 0 0 ${logo.height * scale} 42 744 cm /Logo Do Q`);
    text('fitistic.', 110, 775, { size: 25, bold: true, color: purple });
    text('Gym management & member experience', 110, 756, { size: 9, color: muted });
    text('CONFIDENTIAL', 553, 794, { size: 9, bold: true, align: 'right', color: purple });
    commands.push(pdfLine(42, 728, 553, 728, purple, 2));
    text('SUBSCRIPTION ESTIMATE', 42, 702, { size: 18, bold: true });
    text(`Prepared: ${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 42, 680, { size: 9, color: muted });
    text(`${quote.tier.name} / ${quote.tier.range}`, 42, 657, { size: 11, bold: true });
    text(annual ? 'Annual billing - 12 months, paid upfront' : 'Monthly billing - 1 month', 42, 640, { size: 10, color: muted });
    y = 618;
  };
  const ensure = height => { if (y - height < 82) startPage(); };
  const paragraph = value => {
    for (const line of wrapPdfText(plain(value), 505, 9)) {
      ensure(13); text(line, 42, y - 10, { size: 9, color: muted }); y -= 13;
    }
    y -= 7;
  };
  const tableHeader = () => {
    commands.push(pdfRect(42, y - 28, 511, 28, { fill: purple, stroke: purple }));
    for (const [label, x, align] of [['#', 52, 'left'], ['Description', 77, 'left'], ['Monthly rate', 407, 'right'], ['Months', 452, 'right'], ['Amount', 543, 'right']]) {
      text(label, x, y - 18, { size: 8, bold: true, color: '1 1 1', align });
    }
    y -= 28;
  };
  startPage();
  tableHeader();
  const rows = [{ name: `${quote.tier.name} core subscription`, monthly: quote.tier.monthly }, ...quote.addons];
  rows.forEach((item, index) => {
    const lines = wrapPdfText(item.name, 220, 9, true);
    const height = Math.max(34, lines.length * 12 + 18);
    if (y - height < 82) { startPage(); tableHeader(); }
    commands.push(pdfRect(42, y - height, 511, height, { fill: index % 2 === 0 ? '0.97 0.96 0.99' : '1 1 1' }));
    text(index + 1, 52, y - 21, { size: 9 });
    lines.forEach((line, i) => text(line, 77, y - 21 - i * 12, { size: 9, bold: true }));
    text(money(item.monthly), 407, y - 21, { size: 8, align: 'right' });
    text(annual ? '12' : '1', 452, y - 21, { size: 9, align: 'right' });
    text(money(Math.round(item.monthly * 100) * (annual ? 12 : 1) / 100), 543, y - 21, { size: 8, align: 'right' });
    y -= height;
  });
  y -= 18;
  ensure(annual ? 122 : 80);
  const summary = (label, amount) => {
    text(label, 270, y - 12, { size: 9 });
    text(amount, 543, y - 12, { size: 10, align: 'right' }); y -= 22;
  };
  summary('Subtotal', money(Math.round(quote.monthly * 100) * (annual ? 12 : 1) / 100));
  if (annual) summary(`Annual discount (${catalog.annualDiscountPercent}%)`, `- ${money(quote.savings)}`);
  commands.push(pdfRect(260, y - 34, 293, 34, { fill: '0.94 0.91 0.99', stroke: '0.94 0.91 0.99' }));
  text(annual ? 'Annual total (before tax)' : 'Monthly total (before tax)', 270, y - 21, { size: 9, bold: true });
  text(money(quote.total), 543, y - 21, { size: 11, bold: true, color: purple, align: 'right' });
  y -= 48;
  if (annual) { paragraph(`Monthly equivalent: ${money(quote.effectiveMonthly)}. Annual savings: ${money(quote.savings)}.`); }
  paragraph(catalog.taxNote);
  paragraph('Estimate only - not a tax invoice or payment request. Requirements and final billing will be confirmed before activation.');
  paragraph('Usage credits, AI tokens, payment processing, hardware, installation, advertising and custom work are additional where applicable.');
  pages.forEach((page, index) => {
    page.push(pdfLine(42, 65, 553, 65));
    page.push(pdfText('CONFIDENTIAL - For the intended recipient only.', 42, 48, { size: 8, color: muted }));
    page.push(pdfText(`Page ${index + 1} of ${pages.length}`, 553, 48, { size: 8, align: 'right', color: muted }));
  });
  return pages;
}

export async function downloadEstimatePdf(quote, catalog, annual) {
  const logo = await loadEstimateLogo();
  createPdf('fitistic-plan-estimate.pdf', () => estimatePdfPages(quote, catalog, annual, logo), { image: logo });
}
